import { Component, Input, Output, EventEmitter, TemplateRef, OnChanges, SimpleChanges, OnInit, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface StepperStep {
  key: string;
  label: string;
  content?: TemplateRef<any>;
  nextButtonLabel?: string; // Optional custom label for the Next button on this step
}

@Component({
  selector: 'app-stepper',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './stepper.component.html',
  styleUrl: './stepper.component.scss',
})
export class StepperComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() steps: StepperStep[] = [];
  @Input() currentStep: number = 0;
  @Input() hideNextOnFirstStep: boolean = false;
  @Input() customNextButtonLabel?: string; // Optional custom label for the Next button
  @Input() hideNextButton: boolean = false; // Option to hide the Next button (for custom buttons)
  @Input() disableNextButton: boolean = false; // Option to disable the Next button (for validation)
  @Input() disablePublishButton: boolean = false; // Option to disable the Publish button (for validation)
  @Input() hidePreviousButton: boolean = false; // Option to hide the Previous button
  @Input() disableStepClick?: (stepIndex: number) => boolean; // Optional function to disable step clicking
  @Input() actionsTemplate?: TemplateRef<any>; // Optional extra actions shown in the navigation bar
  /** When true, hide the default Publish button (e.g. when parent provides Preview + Publish in actionsTemplate) */
  @Input() hidePublishButton: boolean = false;
  /** When true, emit next() without advancing; parent must update [currentStep] to advance (enables save-before-advance) */
  @Input() asyncNext: boolean = false;
  /**
   * Pin the navigation buttons to the bottom of the scrolling content area so they stay reachable
   * on long steps (e.g. the dataset metadata builder).
   */
  @Input() floatingNavigation: boolean = false;
  internalCurrentStep: number = 0;
  currentNextButtonLabel: string = 'Next →';

  constructor() {
    this.internalCurrentStep = this.currentStep;
  }
  
  @Output() stepChange = new EventEmitter<number>();
  @Output() next = new EventEmitter<void>();
  @Output() previous = new EventEmitter<void>();

  ngOnInit(): void {
    // Initialize the button label on component init
    this.updateNextButtonLabel();
  }

  ngAfterViewInit(): void {
    // Ensure label is updated after view is initialized
    setTimeout(() => {
      this.updateNextButtonLabel();
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentStep']) {
      this.internalCurrentStep = this.currentStep;
    }
    // Update button label whenever any relevant input changes
    if (changes['currentStep'] || changes['steps'] || changes['customNextButtonLabel']) {
      this.updateNextButtonLabel();
    }
  }

  updateNextButtonLabel(): void {
    // First check if customNextButtonLabel input is provided (highest priority)
    if (this.customNextButtonLabel && this.customNextButtonLabel.trim() !== '') {
      this.currentNextButtonLabel = this.customNextButtonLabel;
      return;
    }
    
    // Then check if nextButtonLabel exists on the current step
    if (this.steps && this.steps.length > 0 && this.internalCurrentStep >= 0 && this.internalCurrentStep < this.steps.length) {
      const currentStep = this.steps[this.internalCurrentStep];
      if (currentStep && currentStep.nextButtonLabel) {
        this.currentNextButtonLabel = currentStep.nextButtonLabel;
        return;
      }
    }
    // Default label
    this.currentNextButtonLabel = 'Next →';
  }

  goToStep(index: number): void {
    if (index >= 0 && index < this.steps.length) {
      this.internalCurrentStep = index;
      this.updateNextButtonLabel();
      this.stepChange.emit(index);
    }
  }

  goToNext(): void {
    if (this.internalCurrentStep < this.steps.length - 1) {
      if (this.asyncNext) {
        this.next.emit();
        return;
      }
      this.internalCurrentStep++;
      this.updateNextButtonLabel();
      this.stepChange.emit(this.internalCurrentStep);
      this.next.emit();
    }
  }

  goToPrevious(): void {
    if (this.internalCurrentStep > 0) {
      this.internalCurrentStep--;
      this.updateNextButtonLabel();
      this.stepChange.emit(this.internalCurrentStep);
      this.previous.emit();
    }
  }

  isStepCompleted(stepIndex: number): boolean {
    return stepIndex < this.internalCurrentStep;
  }

  isStepActive(stepIndex: number): boolean {
    return stepIndex === this.internalCurrentStep;
  }

  canGoNext(): boolean {
    if (this.hideNextOnFirstStep && this.internalCurrentStep === 0) {
      return false;
    }
    return this.internalCurrentStep < this.steps.length - 1;
  }

  canGoPrevious(): boolean {
    return this.internalCurrentStep > 0;
  }

  isStepClickable(stepIndex: number): boolean {
    // Check if step click is disabled via custom function
    if (this.disableStepClick && this.disableStepClick(stepIndex)) {
      return false;
    }
    // Allow clicking on completed steps or the current active step
    return this.isStepCompleted(stepIndex) || this.isStepActive(stepIndex);
  }

  onStepClick(stepIndex: number): void {
    // Only navigate if the step is clickable (completed or active) and not disabled
    if (this.isStepClickable(stepIndex)) {
      this.goToStep(stepIndex);
    }
  }

}

